import Image from 'next/image';
import Link from 'next/link';
import clsx from 'clsx';
import { toDe } from '@/utilites/sku-operations';
import {
  getTextNotification,
  getIconByNotificationType,
  getUrlNotification,
} from '@/utilites/notifications.util';
import { HeaderNotificationsDropdownItemProps } from '@/types/props/components';
import ConditionalComponent from '@/components/common/ConditionalComponent';
import isStringDefined from '@/utilites/is-string-defined';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { CommonEnum } from '@/types/translations/CommonEnum';
import { PrimaryButton } from '@/components/common/PrimaryButton';
import { useState } from 'react';
import ContactFormV2 from '@/components/common/ContactForm/ContactFormV2';

const BaseCalloutStyles = {
  container:
    'tw-bg-white tw-shadow-md tw-rounded-xl tw-p-6 tw-mb-4 tw-border tw-border-solid tw-border-gray-100 tw-transition-all tw-duration-300 hover:tw-shadow-lg tw-group tw-cursor-pointer tw-relative tw-overflow-hidden',
  unreadIndicator:
    'tw-absolute -tw-top-2 -tw-right-2 tw-w-3 tw-h-3 tw-bg-blue-500 tw-rounded-full tw-transition-all tw-duration-300 tw-group-hover:tw-scale-110',
  icon: 'tw-w-16 tw-h-16 tw-object-contain tw-mb-2',
  headline:
    'tw-text-lg tw-font-semibold tw-text-gray-800 tw-mb-1 tw-transition-colors tw-duration-200 group-hover:tw-text-blue-600 group-hover:tw-underline',
  subline: 'tw-text-gray-600 tw-mb-2',
  priceLabel: 'tw-text-gray-500 tw-text-sm',
  priceValue: 'tw-text-gray-900 tw-font-bold tw-ml-1',
};

export default function HeaderNotificationsDropdownItem({
  item,
}: HeaderNotificationsDropdownItemProps) {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const { translate } = useTypedTranslation<CommonEnum>('common');
  const {
    id,
    notificationType,
    headline,
    subline,
    url,
    status: itemStatus,
    value: itemValue,
  } = item;

  const itemUrl = getUrlNotification(url);
  const notificationTypeText = getTextNotification(notificationType);
  const isError = notificationType.toLowerCase() === 'error';

  const renderPriceLabel = () => {
    const labelKey =
      notificationType.toLowerCase() === 'ask'
        ? CommonEnum.askPriceDots
        : notificationType.toLowerCase() === 'bid'
          ? CommonEnum.bidPriceDots
          : CommonEnum.currentPriceDots;

    if (!itemValue || isNaN(Number(itemValue))) return null;

    return (
      <span className={BaseCalloutStyles.priceLabel}>
        {translate(labelKey)}
        <span className={BaseCalloutStyles.priceValue}>{toDe(Number(itemValue || 0))}</span>
      </span>
    );
  };

  return (
    <div
      key={id}
      className={clsx(BaseCalloutStyles.container, !itemStatus && 'tw-ring-2 tw-ring-blue-100')}
    >
      <div className="tw-flex tw-items-center tw-space-x-4">
        {/* Notification Icon and Type */}
        <div className="tw-flex tw-flex-shrink-0 tw-flex-col tw-items-center">
          <Image
            src={getIconByNotificationType(notificationType)}
            width={64}
            height={64}
            alt={`notification-${notificationType}`}
            className={BaseCalloutStyles.icon}
          />
          <span className="tw-text-xs tw-font-medium tw-uppercase tw-text-gray-500">
            {notificationTypeText}
          </span>
        </div>

        {/* Notification Content */}
        <div className="tw-relative tw-flex-grow">
          {/* Headline with optional link */}
          <ConditionalComponent
            condition={isStringDefined(itemUrl)}
            trueComponent={
              <Link href={itemUrl!} className="tw-group tw-no-underline">
                <p className={BaseCalloutStyles.headline}>{headline}</p>
              </Link>
            }
            falseComponent={<p className={BaseCalloutStyles.headline}>{headline}</p>}
          />

          {/* Subline */}
          <p className={BaseCalloutStyles.subline}>{subline}</p>

          {/* Error Contact Support Button */}
          {isError && (
            <PrimaryButton onClick={() => setIsContactFormOpen(true)}>
              {translate(CommonEnum.contact_support)}
            </PrimaryButton>
          )}

          {/* Price Information */}
          {itemValue !== undefined && <div className="tw-mt-3">{renderPriceLabel()}</div>}

          {/* Unread Indicator */}
          {!itemStatus && <div className={BaseCalloutStyles.unreadIndicator} />}
        </div>
      </div>

      {/* Contact Form Modal */}
      <ContactFormV2 isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />
    </div>
  );
}
